import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../tokens/theme';
import { deviceWidthMeasurements } from '../../../style/size';
import { FeedbackSection } from '../common/FeedbackSection';
import { ErrorMessageOnFetchConversation } from '../common/ErrorMessage';
import { LandingPageContent } from '../common/LandingPageContent';
import { InputBar } from '../common/InputBar';
import DesktopMessagesSection from './DesktopMessagesSection';
import {
    EnrichedProductDiscoveryConversationMessage,
    MerchantProductOffer,
} from '../../../lib/productDiscovery/conversationMessages';

interface DesktopExplorationTabContentProps {
    shouldShowFeedbackSection: boolean;
    setShouldShowFeedbackSection: (value: boolean) => void;
    conversationId: string;
    viewportHeight: number;
    failedToFetchConversation: boolean;
    messages: EnrichedProductDiscoveryConversationMessage[] | undefined;
    inputText: string;
    setInputText: (value: string) => void;
    inputTextRef: React.RefObject<HTMLTextAreaElement>;
    setIsInputBarFocused: (value: boolean) => void;
    sendMessage: () => void;
    isInputBarFocused: boolean;
    isAssistantThinking: boolean;
    setBottomPositionScrollButton: (value: number) => void;
    messagesListRef: React.RefObject<HTMLDivElement | null>;
    selectedConversationHistoryItemId?: string;
    resetConversation: ({
        conversationToReopen,
    }: {
        conversationToReopen?: {
            conversationId: string;
            messages: EnrichedProductDiscoveryConversationMessage[];
        };
    }) => void;
    fetchMoreProductSuggestions: (messageId: string, currentQueryScrollOffsets: number[] | undefined) => void;
    isFetchingMoreProductSuggestions: boolean;
    isFromSerpEmbedding: boolean;
    savedMerchantProductOffers: MerchantProductOffer[];
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    sentMessageTimestampRef: React.MutableRefObject<{ messageIndex: number; timestamp: number } | undefined>;
    isReadOnlyConversation: boolean;
}

export function DesktopExplorationTab({
    shouldShowFeedbackSection,
    setShouldShowFeedbackSection,
    conversationId,
    viewportHeight,
    failedToFetchConversation,
    messages,
    inputText,
    setInputText,
    inputTextRef,
    setIsInputBarFocused,
    sendMessage,
    isInputBarFocused,
    isAssistantThinking,
    setBottomPositionScrollButton,
    messagesListRef,
    selectedConversationHistoryItemId,
    resetConversation,
    fetchMoreProductSuggestions,
    isFetchingMoreProductSuggestions,
    isFromSerpEmbedding,
    savedMerchantProductOffers,
    setSavedMerchantProductOffers,
    sentMessageTimestampRef,
    isReadOnlyConversation,
}: DesktopExplorationTabContentProps) {
    return (
        <>
            <FeedbackSection
                {...{
                    shouldShowFeedbackSection,
                    setShouldShowFeedbackSection,
                    conversationId,
                }}
            />
            <ConversationContainer $viewportHeight={viewportHeight}>
                {failedToFetchConversation ? (
                    <ErrorMessageOnFetchConversation />
                ) : (
                    <>
                        {messages === undefined ? (
                            <LandingPageContentContainer>
                                <LandingPageContent {...{ sendMessage, isInputBarFocused }} />
                                <InputBar
                                    {...{
                                        inputText,
                                        setInputText,
                                        inputTextRef,
                                        setIsInputBarFocused,
                                        sendMessage,
                                        isAssistantThinking,
                                        setBottomPositionScrollButton,
                                        isLandingPage: true,
                                    }}
                                />
                            </LandingPageContentContainer>
                        ) : (
                            <DesktopMessagesSection
                                {...{
                                    messages,
                                    messagesListRef,
                                    conversationId,
                                    selectedConversationHistoryItemId,
                                    inputText,
                                    setInputText,
                                    inputTextRef,
                                    setIsInputBarFocused,
                                    sendMessage,
                                    resetConversation,
                                    isAssistantThinking,
                                    setBottomPositionScrollButton,
                                    fetchMoreProductSuggestions,
                                    isFetchingMoreProductSuggestions,
                                    isFromSerpEmbedding,
                                    setShouldShowFeedbackSection,
                                    savedMerchantProductOffers,
                                    setSavedMerchantProductOffers,
                                    sentMessageTimestampRef,
                                    isReadOnlyConversation,
                                }}
                            />
                        )}
                    </>
                )}
            </ConversationContainer>
        </>
    );
}

const ConversationContainer = styled.div<{
    $viewportHeight: number;
}>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: ${({ $viewportHeight }) => `calc(${$viewportHeight}px - 52px)`};
    align-items: center;
    background-color: ${colors.background.light};
`;

const LandingPageContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 45%;
    margin-top: 24px;
    padding-top: 112px;
    padding-left: 64px;
    padding-right: 64px;
    border-radius: 12px 12px 0px 0px;
    background-color: ${colors.background.default};

    @media (max-width: ${deviceWidthMeasurements.xLarge}) {
        width: 60%;
    }

    @media (max-width: ${deviceWidthMeasurements.large}) {
        width: 70%;
    }
`;
